import React, { useEffect, useMemo, useRef, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

import LoadingBlock from "../LoadingBlock/LoadingBlock";
import { baseChartOptions } from "./charts.config";

import exportingModule from "highcharts/modules/exporting";
import offlineExportingModule from "highcharts/modules/offline-exporting";
import exportDataModule from "highcharts/modules/export-data";

function initModule(mod, H) {
  const fn = mod?.default || mod;
  if (typeof fn === "function") fn(H);
}

initModule(exportingModule, Highcharts);
initModule(offlineExportingModule, Highcharts);
initModule(exportDataModule, Highcharts);

function prettyQualLabel(name) {
  const raw = String(name || "").trim();

  const map = {
    POSITIVA: "Positiva",
    NEGATIVA: "Negativa",
    NEUTRA: "Neutra",
  };

  if (map[raw]) return map[raw];

  return raw.toLowerCase().replace(/^\p{L}/u, (m) => m.toUpperCase());
}

export default function QualitativeDonutChart({
  data = [],
  loading = false,
  title = "Distribuição qualitativa",
  filename = "distribuicao-qualitativa",
  exportMode = false,
}) {
  const chartRef = useRef(null);

  const CARD_MIN_H = 360;
  const CHART_H = 360;

  const [chartKey, setChartKey] = useState(0);
  useEffect(() => {
    const onEnd = () => setChartKey((k) => k + 1);
    window.addEventListener("dashboard-export:end", onEnd);
    return () => window.removeEventListener("dashboard-export:end", onEnd);
  }, []);

  const wrapStyle = useMemo(() => {
    if (!exportMode) {
      return {
        position: "relative",
        borderRadius: 12,
        padding: 12,
        border: "none",
        background: "var(--background-graphic)",
        boxShadow: "0 8px 20px rgba(15, 23, 42, 0.05)",
        overflow: "hidden",
        minHeight: CARD_MIN_H,
      };
    }

    return {
      position: "relative",
      borderRadius: 0,
      padding: 0,
      border: "none",
      background: "var(--background-graphic)",
      boxShadow: "none",
      overflow: "visible",
      minHeight: 560,
      width: "100%",
    };
  }, [exportMode]);

  const options = useMemo(() => {
    return {
      ...baseChartOptions,

      chart: {
        type: "pie",
        height: exportMode ? 560 : CHART_H,
        spacing: exportMode ? [30, 130, 30, 130] : [24, 16, 16, 24],
        backgroundColor: "transparent",
        animation: !exportMode,
        marginTop: exportMode ? 0 : 64,
      },

      title: {
        text: exportMode ? null : title,
        align: "left",
        x: 0,
        y: -3,
        margin: 12,
        style: { fontSize: "14px", fontWeight: "800", color: "#614a9e" },
      },

      exporting: {
        enabled: false,
      },

      plotOptions: {
        pie: {
          innerSize: exportMode ? "68%" : "65%",
          size: exportMode ? "68%" : "75%",
          center: exportMode ? ["50%", "50%"] : ["49%", "50%"],
          borderWidth: 0,

          dataLabels: {
            enabled: true,
            distance: exportMode ? 10 : 14,
            connectorWidth: 1,
            connectorPadding: 6,
            crop: false,
            overflow: "allow",
            softConnector: true,
            allowOverlap: true,

            style: {
              fontWeight: "700",
              fontSize: exportMode ? "12px" : "12px",
              color: "var(--text-color)",
              textOutline: "none",
            },

            formatter: function () {
              const pct = Highcharts.numberFormat(this.percentage, 0);
              return `${prettyQualLabel(this.point.name)}: ${this.y} (${pct}%)`;
            },
          },
        },
      },

      tooltip: {
        useHTML: false,
        pointFormatter: function () {
          return `<b>${prettyQualLabel(this.name)}</b>: ${Highcharts.numberFormat(
            this.percentage,
            1
          )}% (${this.y})`;
        },
      },

      legend: exportMode
        ? { enabled: false }
        : {
            align: "center",
            verticalAlign: "bottom",
            layout: "horizontal",
            itemStyle: {
              fontSize: "11px",
              fontWeight: "800",
              color: "var(--text-color)",
            },
            labelFormatter: function () {
              return `${prettyQualLabel(this.name)} (${this.y})`;
            },
          },

      series: [
        {
          name: "Qualitativa",
          data: (data || []).filter(
            (d) => Number(d?.y ?? 0) > 0 || d?.name === "NEGATIVA"
          ),
        },
      ],

      credits: { enabled: false },
    };
  }, [data, title, exportMode]);

  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart) return;

    const t = setTimeout(() => {
      try {
        chart.reflow();

        if (exportMode) {
          const w = chart.renderTo?.clientWidth || null;
          chart.setSize(w, 560, false);
          chart.reflow();

          if (chart.renderTo) {
            chart.renderTo.style.overflow = "visible";
          }

          if (chart.container) {
            chart.container.style.overflow = "visible";
            const svg = chart.container.querySelector("svg");
            if (svg) {
              svg.style.overflow = "visible";
              svg.setAttribute("overflow", "visible");
            }
          }
        }
      } catch {}
    }, exportMode ? 60 : 0);

    return () => clearTimeout(t);
  }, [exportMode, data]);

  const handleExportPNG = () => {
    const chart = chartRef.current?.chart;
    if (!chart) return;

    chart.exportChart(
      { type: "image/png", filename },
      { chart: { backgroundColor: "var(--background-graphic)" } }
    );
  };

  const exportBtnStyle = {
    position: "absolute",
    top: 10,
    right: 10,
    zIndex: 5,

    height: 34,
    padding: "0 12px",
    borderRadius: 12,
    border: "none",
    background: "var(--bottom-dropdown-color)",
    color: "var(--text-color)",
    fontWeight: 900,
    fontSize: 12,

    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    cursor: "pointer",
    userSelect: "none",
    transition: "transform .08s ease",
  };

  const exportBtnHoverStyle = {
    boxShadow: "0 6px 14px rgba(97, 74, 158, 0.15)",
    backgroundColor: "#614a9e",
    color: "white",
  };

  const iconStyle = {
    width: 18,
    height: 18,
    display: "grid",
    placeItems: "center",
    borderRadius: 8,
    background:
      "linear-gradient(135deg, var(--icon-gradient-start), var(--icon-gradient-end))",
    color: "var(--color-text-button-action)",
    fontSize: 12,
    lineHeight: 1,
    flex: "0 0 auto",
  };

  return (
    <LoadingBlock loading={loading} label="Carregando distribuição qualitativa...">
      <div style={wrapStyle}>
        {!exportMode ? (
          <ExportButton
            onClick={handleExportPNG}
            style={exportBtnStyle}
            hoverStyle={exportBtnHoverStyle}
            iconStyle={iconStyle}
          />
        ) : null}

        <HighchartsReact
          key={chartKey}
          ref={chartRef}
          highcharts={Highcharts}
          options={options}
          containerProps={{
            style: {
              height: exportMode ? 560 : CHART_H,
              overflow: exportMode ? "visible" : "hidden",
            },
          }}
        />
      </div>
    </LoadingBlock>
  );
}

function ExportButton({ onClick, style, hoverStyle, iconStyle }) {
  const [hover, setHover] = useState(false);

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ ...style, ...(hover ? hoverStyle : {}) }}
      title="Exportar PNG"
    >
      <span style={iconStyle}>⭳</span>
      Exportar PNG
    </button>
  );
}
